import { clients } from '../../util/clients';
import { parseJson, isEmptyObject } from '../../util/shared/vue-global';

const { backendRest } = clients.direct;

export const parseResponse = (response) => {
  let errorResponse;
  const resData = response.data;
  if (resData) {
    const resultData = resData.results;
    if (Array.isArray(resultData) && resultData.length > 0) {
      errorResponse = resultData[0].errors;
    }
  }
  return errorResponse;
};

/* What the server said went wrong, out of the streamed import response.
 *
 * The import route answers with one object per batch, so a failure is not necessarily the
 * first of them - the batch that failed is usually some way into the file, and parseResponse
 * above only ever reads results[0]. That is why a short import could finish with "Imported!":
 * batch 1 succeeded, so results[0] was clean, and nothing looked any further.
 *
 * Returns undefined when the response names no error, which leaves the caller's own wording
 * (or the run marker's) in place rather than inventing one.
 */
export const importBatchErrorMessage = (response) => {
  const { results } = ((response || {}).data || {});
  if (!Array.isArray(results)) {
    return undefined;
  }
  const messages = results
    .reduce((all, r) => all.concat(Array.isArray((r || {}).errors) ? r.errors : []), [])
    .map((e) => (typeof e === 'string' ? e : (e || {}).message))
    .filter((m) => typeof m === 'string' && m.length > 0);
  return messages.length ? messages.join(' ') : undefined;
};

const isResponseHasSingleElement = (response) => Array.isArray(response) && response.length === 1;

export const parseErrorResult = (response) => {
  const { programType } = response;
  let additionalErrors = {};

  if (!isEmptyObject(response[programType])
    && Object.prototype.hasOwnProperty.call(response[programType], 'data')) {
    additionalErrors = {
      key: programType,
      errors: response[programType].data.length > 0 ? response[programType].data : [],
      proceedImport: response[programType].proceedImport,
    };
  }
  const error = {
    generalErrors: (response.errors || []).length > 0 ? response.errors : [],
    additionalErrors,
    preview: Array.isArray(response.preview) ? response.preview : [],
    hasErrors: !!response.hasErrors,
    hasBadDates: !!response.hasBadDates,
    programType,
  };
  return error;
};

export const parseError = (response) => {
  const errorResponse = parseResponse(response);
  let dialog = false;
  let errorMode;
  let errors = [];
  if (errorResponse) {
    if (isResponseHasSingleElement(errorResponse)) {
      const [jsonResponse, parseStatus] = parseJson(errorResponse);
      errors = jsonResponse;
      if (parseStatus) {
        errorMode = 'errorWithHeader';
        errors = parseErrorResult(errors);
      }
    } else {
      errors = errorResponse;
    }
    dialog = true;
  }
  return [dialog, errors, errorMode];
};

export const uploadPercent = (progEvt) => {
  const { loaded, total } = (progEvt || {});
  if (!total || !loaded) {
    return 0;
  }
  return Math.min(100, Math.round((loaded / total) * 100));
};

// The import route answers in JSON-lines: the destination writes one object per batch as that batch
// lands, and the stringify transform terminates each with a newline. So the newlines in the partial
// response body count the batches the server has finished. A streamed response has no
// content-length, so there is no honest percentage for this phase -- the batch count is the progress.
// Kept as a closure over the last offset read, because the handler fires per chunk and rescanning the
// whole body every time turns this into O(n^2) on a big import.
export const batchCounter = () => {
  let seen = 0;
  let batches = 0;
  return (progEvt) => {
    let text = '';
    try {
      text = ((progEvt || {}).target || {}).responseText || '';
    } catch (e) {
      return batches; // a responseType other than text makes responseText unreadable
    }
    if (text.length < seen) {
      seen = 0;
      batches = 0;
    }
    for (let i = seen; i < text.length; i += 1) {
      if (text[i] === '\n') {
        batches += 1;
      }
    }
    seen = text.length;
    return batches;
  };
};

// Sources whose first line is column names rather than data -- see createSourceToJsonTransforms on
// the backend: 'csv' and 'csv-ignore-header' both parse with noheader:false, the other sources treat
// every line as a row.
const HEADER_SOURCES = ['csv', 'csv-ignore-header'];

export const sourceHasHeader = (source) => HEADER_SOURCES.indexOf(source) > -1;

// json-to-gql.js batches every batchSize rows and pushes one result per batch, falling back to 200
// when the profile does not set its own. Rows + batch size is all it takes to turn the batch count
// the response streams back into a real percentage.
export const DEFAULT_BATCH_SIZE = 200;

export const batchSizeOf = (profileBatchSize) => Number(profileBatchSize) || DEFAULT_BATCH_SIZE;

export const rowsToBatches = (rows, batchSize) => {
  if (!rows || rows < 1) {
    return 0;
  }
  return Math.ceil(rows / batchSizeOf(batchSize));
};

// k finished batches means exactly k*batchSize rows are through, except for the last batch which is
// partial -- hence the clamp to the total.
export const rowsCompleted = (batches, totalRows, batchSize) => {
  if (!batches || batches < 1) {
    return 0;
  }
  return Math.min(totalRows, batches * batchSizeOf(batchSize));
};

// Grouped by thousands so a big import reads at a glance. Done here rather than with toLocaleString
// so the text does not shift with the runtime locale, and not with amountToNumber, which is a
// currency parser that strips the separators back out again.
export const groupThousands = (value) => `${Math.round(Number(value) || 0)}`
  .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// "688 of 2,050 rows", or just "688 rows" when the file could not be counted up front
const importRunRows = (run) => (run.expectedRows > 0
  ? `${groupThousands(run.stagedRows)} of ${groupThousands(run.expectedRows)} rows`
  : `${groupThousands(run.stagedRows)} rows`);

// What a results screen tells the operator about a staged import run (its marker from the server),
// as { type, message } for an alert, or undefined when there is nothing to say. Only 'complete'
// lets anything be marked paid; every other state is the reason Mark Paid is off.
export const importRunSummary = (run) => {
  if (!run) {
    return undefined;
  }
  const rows = importRunRows(run);
  switch (run.status) {
    case 'importing':
      return {
        type: 'info',
        message: `Still importing: ${rows} loaded so far. Mark Paid stays off until the import `
          + 'finishes. This page updates by itself.',
      };
    case 'complete':
      if (run.expectedRows > 0 && run.stagedRows !== run.expectedRows) {
        return {
          type: 'warning',
          message: `Imported ${rows}. The row count does not match the file - review both lists `
            + 'before marking anything paid.',
        };
      }
      return { type: 'success', message: `Imported ${rows}.` };
    case 'failed':
      return {
        type: 'error',
        message: `The import stopped before it finished: ${rows} loaded${run.error ? ` (${run.error})` : ''}. `
          + 'Mark Paid is off. Import the file again.',
      };
    case 'interrupted':
      return {
        type: 'error',
        message: `The import stopped responding after ${rows} loaded. Mark Paid is off. `
          + 'Import the file again.',
      };
    default:
      return undefined;
  }
};

// The error shown when the import request itself throws (network error, gateway timeout, aborted
// stream). The server keeps loading after the browser's connection drops, so the marker is what
// says how far it really got. `run` is the marker: undefined when it could not be read, null when
// this kind of import records none.
export const importFailureMessage = (run) => {
  const unknown = 'There was a problem while importing your file, and the server did not report how many '
    + 'rows were loaded. Check the results before relying on this import, or import the file again.';
  if (!run) {
    return unknown;
  }
  const rows = importRunRows(run);
  switch (run.status) {
    case 'importing':
      return `The connection dropped, but the server is still loading the file: ${rows} so far. `
        + 'Wait for it to finish, then open the results - they say when the import is complete.';
    case 'complete':
      if (run.expectedRows > 0 && run.stagedRows !== run.expectedRows) {
        return `The connection dropped. The server finished, but loaded ${rows}, which does not match `
          + 'the file. Check the results carefully before marking anything paid.';
      }
      return `The connection dropped, but the server finished loading the file (${rows}). `
        + 'You can open the results.';
    case 'failed':
    case 'interrupted':
      return `The import did not finish: ${rows} loaded${run.error ? ` (${run.error})` : ''}. `
        + 'Do not mark anything paid from it. Import the file again.';
    default:
      return unknown;
  }
};

const LF = 10;
const CR = 13;
const SLICE_BYTES = 4 * 1024 * 1024;

// off window rather than the bare global: same object in the browser, and the unit test environment
// only exposes it there
const readSliceBytes = (blob) => new Promise((resolve, reject) => {
  const reader = new window.FileReader();
  reader.onload = () => resolve(new Uint8Array(reader.result));
  reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
  reader.readAsArrayBuffer(blob);
});

// Counts the rows the import will produce, by scanning bytes rather than decoding text: 0x0A never
// appears inside a multi-byte UTF-8 character, so a slice boundary cannot split a newline and there
// is no decode cost. Blank lines are not counted, because csvtojson emits no row for them. Read a
// slice at a time so a large file never lands in memory whole.
export const countFileRows = async (file, { hasHeader = false } = {}) => {
  const canRead = (typeof window !== 'undefined') && !!window.FileReader;
  if (!file || !file.size || (typeof file.slice !== 'function') || !canRead) {
    return 0;
  }
  let lines = 0;
  let lineHasContent = false;
  for (let start = 0; start < file.size; start += SLICE_BYTES) {
    const end = Math.min(start + SLICE_BYTES, file.size);
    // deliberately sequential -- the point is to hold one slice at a time
    // eslint-disable-next-line no-await-in-loop
    const bytes = await readSliceBytes(file.slice(start, end));
    for (let i = 0; i < bytes.length; i += 1) {
      const byte = bytes[i];
      if (byte === LF) {
        if (lineHasContent) {
          lines += 1;
        }
        lineHasContent = false;
      } else if (byte !== CR) {
        lineHasContent = true;
      }
    }
  }
  if (lineHasContent) {
    lines += 1;
  }
  return (hasHeader && lines > 0) ? lines - 1 : lines;
};

export const filePreValidate = async (params) => {
  const {
    form,
    formHeaders,
    onUploadProgress,
    onDownloadProgress,
  } = params;
  const response = await backendRest.post('/transmap/import', form, {
    headers: formHeaders,
    onUploadProgress,
    onDownloadProgress,
  });

  const [dialog, errors, errorMode] = parseError(response);
  return [dialog, errors, errorMode];
};

export const importFileProgramMapperWithBackup = ({
  OD: 'odtransactions',
  WAR: 'wartransactions',
  PAYROLL: 'payrolltransactions',
  MISC: 'misctransactions',
  MTG: 'mtgtransactions',
});

export default {};
